import React, { useEffect, useRef, useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

interface Review {
  name: string;
  text: string;
  rating: number;
}

const reviews: Review[] = [
  {
    name: 'Liam Birchall',
    text: 'Highly recommend and will use again. Amazing service from start to finish with our project. Supportive throughout and very accommodating. Very patient with ourselves who wouldn\'t have known where to start!',
    rating: 5,
  },
  {
    name: 'Lucy Charlotte Nicholls',
    text: "Sooo grateful to have found Sean. Sean's been super understanding from the start and worked really hard (and fast) to bring my vision to life. Totally recommend! A+++",
    rating: 5,
  },
  {
    name: 'Master Maths IOM',
    text: 'I am absolutely thrilled with the website designed for my business Master Maths. From start to finish the entire process was efficient and incredibly impressive. The patience Sean demonstrated throughout was truly remarkable. The website was up and running far quicker than I anticipated and I now have a remarkable platform to showcase Master Maths.',
    rating: 5,
  },
  {
    name: 'Siscaro Property Solutions',
    text: 'From the very beginning of our discussions, the entire process was handled smoothly, efficiently, and with a friendly, professional approach. Within just a few days, everything was completed to a high standard. I would highly recommend AI & Web Solutions IOM to anyone seeking reliable and high-quality web development services.',
    rating: 5,
  },
  {
    name: 'Bonzai Pan Asian Kitchen',
    text: 'AI & Web Solutions IOM provided outstanding service from start to finish. Communication was quick and clear, and our website issues were resolved in just a few days. The results were excellent, and their can-do attitude made the whole process stress-free. Nothing was too hard for them to deal with.',
    rating: 5,
  },
];

const ReviewCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const startAutoPlay = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, 5000);
  };

  useEffect(() => {
    if (isAutoPlaying) startAutoPlay();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoPlaying]);

  const goTo = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goNext = () => goTo((currentIndex + 1) % reviews.length);
  const goPrev = () => goTo((currentIndex - 1 + reviews.length) % reviews.length);

  const current = reviews[currentIndex];

  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="relative">
          {/* Card */}
          <div className="relative p-8 sm:p-10 lg:p-12 rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
            <div className="absolute top-6 right-8 opacity-[0.04]">
              <Quote className="h-24 w-24 text-white" />
            </div>

            <div className="relative z-10">
              {/* Stars */}
              <div className="flex gap-0.5 mb-6">
                {[...Array(current.rating)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-primary/80 fill-primary/80" />
                ))}
              </div>

              {/* Review text */}
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-8 min-h-[80px]">
                "{current.text}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium text-sm">{current.name}</p>
                  <p className="text-gray-600 text-xs mt-0.5">Recommends AI & Web Solutions IOM</p>
                </div>

                {/* Navigation arrows */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={goPrev}
                    className="w-8 h-8 rounded-lg bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-gray-500 hover:text-white hover:border-white/10 transition-all duration-300"
                    aria-label="Previous review"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={goNext}
                    className="w-8 h-8 rounded-lg bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-gray-500 hover:text-white hover:border-white/10 transition-all duration-300"
                    aria-label="Next review"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-6 bg-primary/70'
                    : 'w-1.5 bg-white/10 hover:bg-white/20'
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewCarousel;
